// Rail styles: four ways to show the same personal rail layout (railLayout.js), picked per user in
// Customize menu and saved with it (userPrefs/{uid}/railLayout.style):
//   list         every group and link, as it's always been
//   collapsible  group headers fold their links away; the group you're in stays open, and a folded
//                group shows the total of its counts
//   icons        a slim rail of group buttons; each opens a fly-out with that group's links
//   pinned       only the links you pin, plus All pages (Ctrl+K): every link by group, with ☆ to pin
// On a phone-width screen the rail is the slide-out menu, so it is always shown as the list.
//
// The rail's own buttons are never rebuilt (app.js wired their clicks and other code .click()s
// them): links a style doesn't show move to an off-stage holder, and fly-outs and All pages show
// stand-ins that click the real button.

import { escapeHTML } from './utilities';
import { isHub, hubTabs } from './railHubs';

export const RAIL_STYLES = [
  { id: 'list', name: 'Full list', note: 'Every group and link, as it is today.' },
  { id: 'collapsible', name: 'Collapsible groups', note: 'Groups fold away; the one you’re in stays open.' },
  { id: 'icons', name: 'Icon rail', note: 'A slim rail of groups; each opens a menu. More room for pages.' },
  { id: 'pinned', name: 'Pinned + All pages', note: 'Only the links you pin; everything else in All pages (Ctrl+K).' },
];
const STYLE_IDS = RAIL_STYLES.map(s => s.id);
const OPEN_KEY = 'railOpenGroups:v1'; // collapsible: groups the user opened (this device only)
const PHONE = window.matchMedia('(max-width: 768px)');

const S = { layout: null, style: 'list', groups: [], onPin: null, observer: null, fly: null, launcher: null };
const bar = () => document.querySelector('#buttonBar .page-buttons');
const esc = v => escapeHTML(v == null ? '' : String(v));
/** Where generated rail entries go: before the off-stage holders that close the list. */
const anchor = () => bar().querySelector(':scope > #rail-hidden-items, :scope > #rail-hub-members, :scope > #rail-offstage');

function offstage() {
  let h = document.getElementById('rail-offstage');
  if (!h) {
    h = document.createElement('div');
    h.id = 'rail-offstage';
    h.hidden = true;
    bar().appendChild(h);
  }
  return h;
}

const labelOf = el => [...el.querySelectorAll('.rail-label')].map(l => l.textContent.trim()).filter(Boolean).join(' & ') || el.getAttribute('aria-label') || '';
const iconOf = el => el.querySelector('img.icon-brand')?.getAttribute('src') || '';
const countOf = el => [...el.querySelectorAll('.rail-count')].filter(c => !c.hidden).reduce((t, c) => t + (parseInt(c.textContent, 10) || 0), 0);
const isSelected = el => el.classList.contains('selected-tide-button') || !!el.querySelector('.selected-tide-button');
/** The real buttons inside a rail entry (most entries are one button). */
const buttonsOf = el => (el.tagName === 'BUTTON' ? [el] : [...el.querySelectorAll('button')]);
/** Only write when it changes: every write wakes the watcher below. */
const setText = (n, v) => { if (n.textContent !== v) n.textContent = v; };
const setHidden = (n, v) => { if (n.hidden !== v) n.hidden = v; };
const shown = key => { const el = document.getElementById(key); return el && !el.classList.contains('feature-off') ? el : null; };

/** Groups as they show: [{ name, els: [element] }] (hidden and switched-off links left out). */
function visibleGroups(layout) {
  return layout.groups.map(g => ({ name: g.name, els: g.items.map(shown).filter(Boolean) })).filter(g => g.els.length);
}

function setBodyStyle(style) {
  STYLE_IDS.forEach(id => document.body.classList.toggle(`rail-style-${id}`, id === style));
}

/** Undo whatever the last style added (railLayout's render() has just put the links back in order). */
function reset() {
  closeFly();
  bar().querySelectorAll('.rs-gen').forEach(n => n.remove());
  bar().querySelectorAll('.rail-group-header').forEach(h => {
    h.classList.remove('rs-fold-head', 'rs-folded-head');
    h.removeAttribute('role'); h.removeAttribute('tabindex'); h.removeAttribute('aria-expanded');
    h.onclick = h.onkeydown = null;
  });
  bar().querySelectorAll('.rs-folded').forEach(el => el.classList.remove('rs-folded'));
}

/**
 * Show `layout` in its style. Called by railLayout.js after every render (the links are then in
 * the rail in layout order). onPin(key, pinned) saves a ☆ from All pages.
 */
export function applyRailStyle(layout, { onPin } = {}) {
  if (!bar()) return;
  S.layout = layout;
  S.onPin = onPin || S.onPin;
  reset();
  const want = STYLE_IDS.includes(layout.style) ? layout.style : 'list';
  S.style = PHONE.matches ? 'list' : want;
  setBodyStyle(S.style);
  S.groups = visibleGroups(layout);
  if (S.style === 'collapsible') applyCollapsible();
  if (S.style === 'icons') applyIcons();
  if (S.style === 'pinned') applyPinned(layout);
  watch();
  refresh();
}

/* ---------- collapsible ---------- */

const openSet = () => { try { return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) || '[]')); } catch { return new Set(); } };
const saveOpen = set => { try { localStorage.setItem(OPEN_KEY, JSON.stringify([...set])); } catch { /* storage blocked */ } };

function applyCollapsible() {
  const heads = [...bar().querySelectorAll(':scope > .rail-group-header')];
  heads.forEach(h => {
    const g = S.groups.find(x => x.name === h.textContent.trim());
    if (!g) return;
    h.classList.add('rs-fold-head');
    h.dataset.rsGroup = g.name;
    h.setAttribute('role', 'button');
    h.tabIndex = 0;
    const badge = document.createElement('span');
    badge.className = 'rs-gen rs-badge';
    badge.hidden = true;
    const chev = document.createElement('span');
    chev.className = 'rs-gen rs-chev';
    chev.setAttribute('aria-hidden', 'true');
    h.append(badge, chev);
    const toggle = () => {
      const set = openSet();
      if (set.has(g.name)) set.delete(g.name); else set.add(g.name);
      saveOpen(set);
      refresh();
    };
    h.onclick = toggle;
    h.onkeydown = e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } };
  });
}

/* ---------- icons ---------- */

function applyIcons() {
  const off = offstage();
  S.groups.forEach((g, i) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rs-gen rs-group-btn';
    btn.dataset.g = String(i);
    btn.setAttribute('aria-haspopup', 'menu');
    btn.setAttribute('aria-expanded', 'false');
    const icon = iconOf(g.els[0]);
    btn.innerHTML = `${icon ? `<img src="${esc(icon)}" alt="" width="20" height="20">` : '<span class="rs-dot" aria-hidden="true"></span>'}
      <span class="rs-group-name">${esc(g.name || 'Menu')}</span><span class="rs-badge" hidden></span>`;
    btn.addEventListener('click', e => { e.stopPropagation(); toggleFly(i, btn); });
    bar().insertBefore(btn, anchor());
  });
  bar().querySelectorAll(':scope > .rail-group-header').forEach(h => h.classList.add('rs-folded-head'));
  S.groups.forEach(g => g.els.forEach(el => off.appendChild(el)));
}

/**
 * Stand-ins for a rail entry, one per real button in it: same icon, name and count; clicking one
 * clicks the real button. With star, the first carries the entry's ☆ (pins are per entry).
 */
function standIns(entry, { star = false, pinned = false } = {}) {
  return buttonsOf(entry).filter(b => b.id).map((b, i) => {
    const n = countOf(b);
    const icon = iconOf(b);
    const name = labelOf(b) || labelOf(entry);
    return `<div class="rs-row${isSelected(b) ? ' active' : ''}">
    <button type="button" class="rail-flyout-item rs-go" data-go="${esc(b.id)}" role="menuitem">
      ${icon ? `<img src="${esc(icon)}" alt="" width="16" height="16">` : '<span class="rs-dot" aria-hidden="true"></span>'}
      <span class="rail-flyout-item-label">${esc(name)}</span>${n ? `<span class="rail-flyout-badge">${n}</span>` : ''}</button>
    ${star && i === 0 ? `<button type="button" class="rs-star${pinned ? ' on' : ''}" data-pin="${esc(entry.id)}" aria-pressed="${pinned}" aria-label="${pinned ? 'Unpin' : 'Pin'} ${esc(labelOf(entry))}" title="${pinned ? 'Unpin from your rail' : 'Pin to your rail'}">${pinned ? '★' : '☆'}</button>` : star ? '<span class="rs-star-gap"></span>' : ''}
  </div>`;
  }).join('');
}

/** All pages: a hub's pages, indented under it, each with its own ☆ (pins it as its own rail link). */
function tabRows(entry, pins, q = '') {
  if (!isHub(entry.id)) return '';
  const hubHit = !q || labelOf(entry).toLowerCase().includes(q);
  return hubTabs(entry.id).filter(t => hubHit || tabHit(t, q)).map(t => {
    const on = pins.has(t.key);
    return `<div class="rs-row rs-sub">
    <button type="button" class="rail-flyout-item rs-go" data-go="${esc(t.key)}" role="menuitem"><span class="rail-flyout-item-label">${esc(t.label)}</span></button>
    <button type="button" class="rs-star${on ? ' on' : ''}" data-pin="${esc(t.key)}" aria-pressed="${on}" aria-label="${on ? 'Unpin' : 'Pin'} ${esc(t.label)}" title="${on ? 'Unpin from your rail' : 'Pin to your rail as its own link'}">${on ? '★' : '☆'}</button>
  </div>`;
  }).join('');
}
const tabHit = (t, q) => t.label.toLowerCase().includes(q) || labelOf(document.getElementById(t.key) || document.createElement('i')).toLowerCase().includes(q);

function go(key) {
  closeFly();
  closeLauncher();
  // The real button opens the page (a hub opens the page last used in it). After this click has
  // finished, so the click doesn't land on whatever opens.
  setTimeout(() => document.getElementById(key)?.click(), 0);
}

function toggleFly(i, btn) {
  if (S.fly && S.fly.i === i) { closeFly(); return; }
  closeFly();
  const g = S.groups[i];
  const panel = document.createElement('div');
  panel.className = 'rail-flyout open rs-fly';
  panel.setAttribute('role', 'menu');
  panel.setAttribute('aria-label', g.name || 'Menu');
  panel.innerHTML = `<div class="rail-flyout-header"><span>${esc(g.name || 'Menu')}</span></div><div class="rail-flyout-body">${g.els.map(el => standIns(el)).join('')}</div>`;
  document.getElementById('buttonBar').appendChild(panel);
  const top = btn.getBoundingClientRect().top - document.getElementById('buttonBar').getBoundingClientRect().top;
  panel.style.top = `${Math.max(12, Math.min(top - 8, window.innerHeight - panel.offsetHeight - 16))}px`;
  panel.addEventListener('click', e => { const b = e.target.closest('[data-go]'); if (b) go(b.dataset.go); });
  btn.setAttribute('aria-expanded', 'true');
  S.fly = { i, panel, btn };
  panel.querySelector('[data-go]')?.focus();
}

function closeFly() {
  if (!S.fly) return;
  S.fly.panel.remove();
  S.fly.btn.setAttribute('aria-expanded', 'false');
  S.fly = null;
}

document.addEventListener('click', e => {
  if (S.fly && !e.target.closest('.rs-fly') && !e.target.closest('.rs-group-btn')) closeFly();
});
document.addEventListener('keydown', e => {
  if (e.key === 'Escape') { closeFly(); closeLauncher(); }
  if (S.style === 'pinned' && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); openLauncher(); }
});

/* ---------- pinned + All pages ---------- */

function applyPinned(layout) {
  const off = offstage();
  const pins = new Set(layout.pinned || []);
  bar().querySelectorAll(':scope > .rail-group-header').forEach(h => h.classList.add('rs-folded-head'));
  const head = document.createElement('div');
  head.className = 'rs-gen rail-group-header';
  head.textContent = 'Pinned';
  bar().insertBefore(head, anchor());
  // Pinned links in the order they were pinned; the rest off stage.
  (layout.pinned || []).map(shown).filter(Boolean).forEach(el => bar().insertBefore(el, anchor()));
  S.groups.forEach(g => g.els.forEach(el => { if (!pins.has(el.id)) off.appendChild(el); }));
  const all = document.createElement('button');
  all.type = 'button';
  all.className = 'rs-gen rs-all';
  all.innerHTML = '<span>All pages</span><kbd>Ctrl K</kbd>';
  all.addEventListener('click', e => { e.stopPropagation(); openLauncher(); });
  bar().insertBefore(all, anchor());
}

function paintLauncher(q = '') {
  const L = S.launcher;
  if (!L) return;
  const pins = new Set(S.layout.pinned || []);
  const match = el => !q || buttonsOf(el).some(b => (labelOf(b) || labelOf(el)).toLowerCase().includes(q))
    || (isHub(el.id) && hubTabs(el.id).some(t => tabHit(t, q)));
  L.querySelector('.rs-launch-grid').innerHTML = S.groups.map(g => {
    const els = g.els.filter(match);
    if (!els.length) return '';
    return `<section class="rs-launch-group"><div class="rs-launch-head">${esc(g.name || 'Menu')}</div>${els.map(el => standIns(el, { star: true, pinned: pins.has(el.id) }) + tabRows(el, pins, q)).join('')}</section>`;
  }).join('') || '<div class="rs-launch-empty">No page by that name.</div>';
}

export function openLauncher() {
  if (S.launcher) return;
  const wrap = document.createElement('div');
  wrap.className = 'rs-launch-scrim';
  wrap.innerHTML = `<div class="rs-launch" role="dialog" aria-modal="true" aria-label="All pages">
    <div class="rs-launch-top"><input type="search" class="rs-launch-q" placeholder="Type a page name…" aria-label="Find a page" autocomplete="off">
      <span class="rs-launch-hint">☆ pins a page to your rail</span><button type="button" class="rs-launch-custom">Customize menu</button><button type="button" class="rs-launch-x" aria-label="Close">✕</button></div>
    <div class="rs-launch-grid"></div></div>`;
  document.body.appendChild(wrap);
  S.launcher = wrap;
  paintLauncher();
  const q = wrap.querySelector('.rs-launch-q');
  q.focus();
  q.addEventListener('input', () => paintLauncher(q.value.trim().toLowerCase()));
  // Enter opens the first page whose own name matches ("dry stack" → Dry stack, not its hub).
  q.addEventListener('keydown', e => {
    if (e.key !== 'Enter') return;
    const v = q.value.trim().toLowerCase();
    const rows = [...wrap.querySelectorAll('[data-go]')];
    const first = rows.find(b => b.querySelector('.rail-flyout-item-label')?.textContent.toLowerCase().includes(v)) || rows[0];
    if (first) go(first.dataset.go);
  });
  wrap.addEventListener('click', e => {
    if (e.target === wrap || e.target.closest('.rs-launch-x')) { closeLauncher(); return; }
    if (e.target.closest('.rs-launch-custom')) { closeLauncher(); setTimeout(() => document.getElementById('rail-customize-button')?.click(), 0); return; }
    const pin = e.target.closest('[data-pin]');
    if (pin) {
      const key = pin.dataset.pin;
      const on = !(S.layout.pinned || []).includes(key);
      S.onPin?.(key, on);
      setTimeout(() => paintLauncher(q.value.trim().toLowerCase()), 0);
      return;
    }
    const b = e.target.closest('[data-go]');
    if (b) go(b.dataset.go);
  });
}

function closeLauncher() {
  if (!S.launcher) return;
  S.launcher.remove();
  S.launcher = null;
}

/* ---------- counts, the page you're on ---------- */

function refresh() {
  if (S.style === 'collapsible') {
    const open = openSet();
    bar().querySelectorAll(':scope > .rs-fold-head').forEach(h => {
      const g = S.groups.find(x => x.name === h.dataset.rsGroup);
      if (!g) return;
      const here = g.els.some(isSelected);
      const isOpen = open.has(g.name) || here;
      h.setAttribute('aria-expanded', String(isOpen));
      g.els.forEach(el => el.classList.toggle('rs-folded', !isOpen));
      const n = g.els.reduce((t, el) => t + countOf(el), 0);
      const badge = h.querySelector('.rs-badge');
      if (badge) { setHidden(badge, isOpen || !n); setText(badge, String(n)); }
    });
  }
  if (S.style === 'icons') {
    bar().querySelectorAll('.rs-group-btn').forEach(btn => {
      const g = S.groups[Number(btn.dataset.g)];
      if (!g) return;
      btn.classList.toggle('rs-active', g.els.some(isSelected));
      const n = g.els.reduce((t, el) => t + countOf(el), 0);
      const badge = btn.querySelector('.rs-badge');
      setHidden(badge, !n);
      setText(badge, n > 99 ? '99+' : String(n));
    });
  }
}

let raf = 0;
function watch() {
  if (S.observer) return;
  // Counts change (shellNav.js) and the page you're on changes (app.js): keep badges and the
  // open group in step.
  S.observer = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; refresh(); }); });
  S.observer.observe(document.getElementById('buttonBar'), { subtree: true, attributes: true, attributeFilter: ['class', 'hidden'], characterData: true, childList: true });
}

PHONE.addEventListener('change', () => { if (S.layout) applyRailStyle(S.layout); });
